import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Building2, MapPin, ShieldCheck, Cpu, Mail, ChevronLeft, ChevronRight, Check,
  Sparkles, Trash2, Plus, Radio,
} from "lucide-react";

const STEPS = [
  { id: 1, label: "Organisation", icon: Building2 },
  { id: 2, label: "First Site",   icon: MapPin },
  { id: 3, label: "Systems",      icon: ShieldCheck },
  { id: 4, label: "Gateway",      icon: Cpu },
  { id: 5, label: "Verify Data",  icon: Radio },
  { id: 6, label: "Team",         icon: Mail },
];

const BUILDING_TYPES = ["Commercial", "Residential", "Industrial", "Hospital", "Hotel", "Mall", "IT Park"];
const SYSTEMS = [
  { key: "hydrant_system",    label: "Hydrant System" },
  { key: "sprinkler_system",  label: "Sprinkler System" },
  { key: "fire_water_tank",   label: "Fire Water Tanks" },
  { key: "detection_system",  label: "Detection System" },
  { key: "ventilation_system",label: "Ventilation System" },
  { key: "lifts_module",      label: "Lifts" },
  { key: "diesel_generator",  label: "Diesel Generator" },
];

const ROLES = [
  { v: "customer_admin",    label: "Organization Admin (full access to this org)" },
  { v: "building_manager",  label: "Building Manager" },
  { v: "site_manager",      label: "Site Manager" },
  { v: "fire_safety_officer", label: "Fire Safety Officer" },
  { v: "viewer",            label: "Viewer (read-only)" },
];

export default function OnboardingWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [subscribedKeys, setSubscribedKeys] = useState(new Set());
  const [data, setData] = useState({
    name: "", gstin: "", address: "", city: "", pin_code: "",
    primary_contact_name: "", primary_contact_phone: "", fire_license_no: "",
    first_site: { name: "", building_type: "Commercial", city: "", total_floors: 0, building_height: 0, occupancy: 0, fire_noc_number: "" },
    systems: [],
    gateway_serial: "",
    invitations: [],
  });
  // idle | checking | found | pending | not_found
  const [gwStatus, setGwStatus] = useState("idle");
  const [gwInfo, setGwInfo]     = useState(null);
  const [ioMappings, setIoMappings] = useState([]);

  useEffect(() => {
    api.get("/billing/my-subscription").then((r) => {
      setSubscribedKeys(new Set(r.data.modules.filter((m) => m.is_enabled).map((m) => m.module_key)));
    }).catch(() => {});
  }, []);

  const verifyGateway = async () => {
    const serial = data.gateway_serial.trim();
    if (!serial) { toast.error("Enter a gateway serial / IMEI first"); return; }
    setGwStatus("checking");
    try {
      const r = await api.get("/hardware/gateways/check", { params: { serial } });
      const info = r.data;
      setGwInfo(info);
      if (info.registered && info.status === "online") setGwStatus("found");
      else if (info.registered) setGwStatus("pending");
      else setGwStatus("not_found");
    } catch { setGwStatus("not_found"); }
  };

  const errors = {
    1: !data.name ? "Company name is required" : data.gstin && data.gstin.length !== 15 ? "GSTIN must be 15 characters" : null,
    2: !data.first_site.name ? "Site name is required" : null,
    3: data.systems.length === 0 ? "Select at least one system" : null,
    4: null,
    5: null,
    6: null,
  };

  const next = () => {
    if (errors[step]) { toast.error(errors[step]); return; }
    if (step === 4 && gwStatus === "found" && data.gateway_serial) {
      api.get(`/hardware/gateways/${data.gateway_serial.trim()}/io-mappings`).then((r) => setIoMappings(r.data)).catch(() => setIoMappings([]));
    }
    setStep(step + 1);
  };
  const back = () => setStep(Math.max(1, step - 1));

  const toggleSystem = (k) => {
    setData({ ...data, systems: data.systems.includes(k) ? data.systems.filter((s) => s !== k) : [...data.systems, k] });
  };

  const addInvite = () => setData({ ...data, invitations: [...data.invitations, { email: "", role: "viewer", name: "" }] });
  const updateInvite = (i, patch) => {
    const copy = [...data.invitations]; copy[i] = { ...copy[i], ...patch };
    setData({ ...data, invitations: copy });
  };
  const removeInvite = (i) => setData({ ...data, invitations: data.invitations.filter((_, idx) => idx !== i) });

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/tenant/org/onboarding", data);
      toast.success(`Welcome to Fire360Pro, ${data.name}!`);
      nav(data.gateway_serial ? "/gateways?onboarded=1" : "/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };

  const pct = Math.round((step / STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-slate-950 text-gray-900" data-testid="onboarding-wizard">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-7 h-7 text-orange-400" strokeWidth={2.5} />
          <h1 className="text-3xl font-extrabold">Welcome to Fire360Pro</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">Set up your ACMS environment in 5 quick steps · Maharashtra GR MFS-2023 compliant</p>

        {/* PROGRESS */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <div key={s.id} className="flex flex-col items-center flex-1" data-testid={`step-indicator-${s.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${done ? "bg-emerald-500 text-white" : active ? "f360-gradient text-white" : "bg-white/10 text-slate-400"}`}>
                    {done ? <Check className="w-5 h-5" strokeWidth={2.5} /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mt-1.5 ${active ? "text-orange-300" : done ? "text-emerald-300" : "text-gray-500"}`}>{s.label}</div>
                </div>
              );
            })}
          </div>
          <div className="h-2 bg-gray-50 rounded-full overflow-hidden">
            <div className="h-full f360-gradient transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* STEP BODY */}
        <div className="glass rounded-lg p-6">
          {step === 1 && (
            <div className="space-y-4" data-testid="step-1">
              <h2 className="text-xl font-bold mb-2">Organisation Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Company Name *</Label><Input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" data-testid="onb-name" /></div>
                <div><Label>GSTIN (15-char)</Label><Input value={data.gstin} maxLength={15} onChange={(e) => setData({ ...data, gstin: e.target.value.toUpperCase() })} className="bg-gray-50 border-gray-200 mt-1 font-mono" data-testid="onb-gstin" /></div>
                <div><Label>Fire License Number</Label><Input value={data.fire_license_no} onChange={(e) => setData({ ...data, fire_license_no: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div className="col-span-2"><Label>Address</Label><Input value={data.address} onChange={(e) => setData({ ...data, address: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>City</Label><Input value={data.city} onChange={(e) => setData({ ...data, city: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>PIN Code</Label><Input value={data.pin_code} maxLength={6} onChange={(e) => setData({ ...data, pin_code: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>Contact Person</Label><Input value={data.primary_contact_name} onChange={(e) => setData({ ...data, primary_contact_name: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>Phone</Label><Input value={data.primary_contact_phone} onChange={(e) => setData({ ...data, primary_contact_phone: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="+91 98XXX XXXXX" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-2">
              <h2 className="text-xl font-bold mb-2">First Site Setup</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Site Name *</Label><Input value={data.first_site.name} onChange={(e) => setData({ ...data, first_site: { ...data.first_site, name: e.target.value } })} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Phoenix Marketcity Mall" data-testid="onb-site-name" /></div>
                <div><Label>Building Type</Label>
                  <Select value={data.first_site.building_type} onValueChange={(v) => setData({ ...data, first_site: { ...data.first_site, building_type: v } })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">{BUILDING_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>City</Label><Input value={data.first_site.city} onChange={(e) => setData({ ...data, first_site: { ...data.first_site, city: e.target.value } })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>No. of Floors</Label><Input type="number" min={0} value={data.first_site.total_floors} onChange={(e) => setData({ ...data, first_site: { ...data.first_site, total_floors: Number(e.target.value) } })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>Building Height (m)</Label><Input type="number" min={0} step={0.5} value={data.first_site.building_height} onChange={(e) => setData({ ...data, first_site: { ...data.first_site, building_height: Number(e.target.value) } })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>Occupancy (persons)</Label><Input type="number" min={0} value={data.first_site.occupancy} onChange={(e) => setData({ ...data, first_site: { ...data.first_site, occupancy: Number(e.target.value) } })} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div><Label>Fire NOC Number</Label><Input value={data.first_site.fire_noc_number} onChange={(e) => setData({ ...data, first_site: { ...data.first_site, fire_noc_number: e.target.value } })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-3">
              <h2 className="text-xl font-bold mb-2">Systems Present at Site</h2>
              <p className="text-xs text-gray-500">Select all systems installed at your building. Modules not subscribed will appear locked.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SYSTEMS.map((s) => {
                  const subscribed = subscribedKeys.has(s.key) || s.key === "diesel_generator";
                  const active = data.systems.includes(s.key);
                  return (
                    <button key={s.key} onClick={() => subscribed && toggleSystem(s.key)} disabled={!subscribed}
                      className={`relative p-4 rounded-lg border text-left transition-all ${
                        active ? "border-orange-500/60 bg-orange-500/10" :
                        subscribed ? "border-white/10 bg-white/5 hover:border-orange-500/30" :
                        "border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed"
                      }`} data-testid={`system-${s.key}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold">{s.label}</div>
                        {active && <Check className="w-5 h-5 text-orange-400" strokeWidth={2.5} />}
                        {!subscribed && <span className="text-[10px] uppercase tracking-wider bg-gray-100 px-2 py-0.5 rounded-full">Locked</span>}
                      </div>
                      {!subscribed && <div className="text-[11px] text-gray-500 mt-1">Subscribe to enable</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="step-4">
              <h2 className="text-xl font-bold mb-2">IPC Gateway Pairing</h2>
              <p className="text-sm text-gray-500">Enter the IMEI or serial number printed on your WT-460M gateway. This links incoming device data to your site.</p>

              <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                <div>
                  <Label>Gateway Serial / IMEI</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={data.gateway_serial}
                      onChange={(e) => { setData({ ...data, gateway_serial: e.target.value }); setGwStatus("idle"); setGwInfo(null); }}
                      className="bg-white border-gray-200 font-mono flex-1"
                      placeholder="e.g. 860710086582429"
                      data-testid="gateway-serial"
                    />
                    <Button onClick={verifyGateway} disabled={gwStatus === "checking"} variant="outline" className="border-orange-500/40 text-orange-300 shrink-0">
                      {gwStatus === "checking" ? "Checking…" : "Verify"}
                    </Button>
                  </div>
                </div>

                {gwStatus === "found" && (
                  <div className="flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid="gw-found">
                    <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div>
                      <div className="font-semibold text-emerald-300 text-sm">Device online</div>
                      <div className="text-xs text-gray-400 mt-0.5">{gwInfo?.gateway_name || data.gateway_serial} is actively connected. IO mappings will be pre-filled for you.</div>
                    </div>
                  </div>
                )}
                {gwStatus === "pending" && (
                  <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="gw-pending">
                    <Cpu className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-amber-300 text-sm">Gateway registered — waiting for device</div>
                      <div className="text-xs text-gray-400 mt-0.5">Power on the device and ensure it has network access. Data will start flowing within one poll cycle (~10 s).</div>
                    </div>
                  </div>
                )}
                {gwStatus === "not_found" && (
                  <div className="flex items-start gap-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3" data-testid="gw-not-found">
                    <Cpu className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-blue-300 text-sm">Gateway will be registered on finish</div>
                      <div className="text-xs text-gray-400 mt-0.5">No existing record found for this serial. It will be created when you complete setup. Power on the device after finishing.</div>
                    </div>
                  </div>
                )}

                <p className="text-[11px] text-gray-500">You can skip this step and add gateways later from the Hardware → Gateways page.</p>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="step-5">
              <h2 className="text-xl font-bold mb-2">Verify Live Data</h2>
              <p className="text-sm text-gray-500">These are the registers that will be monitored once setup finishes. You can review or edit them anytime from Hardware → Gateways.</p>

              {gwStatus === "found" && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-testid="verify-io-list">
                  {ioMappings.length ? ioMappings.map((m) => (
                    <div key={m.mapping_id} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2 border border-gray-200">
                      <span className="font-mono text-orange-300">{m.io_point_name || m.io_point_address}</span>
                      <span className="text-xs text-gray-500">{m.signal_type} {m.engineering_unit ? `· ${m.engineering_unit}` : ""}</span>
                    </div>
                  )) : (
                    <div className="text-sm text-gray-500">No IO mappings found yet for this gateway — default mappings will be created automatically for known models when setup finishes.</div>
                  )}
                </div>
              )}

              {(gwStatus === "pending" || gwStatus === "not_found") && (
                <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <Radio className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-amber-300 text-sm">No live data yet</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {gwStatus === "pending"
                        ? "Your gateway is registered but hasn't sent data yet. Once powered on, IO mappings will appear here automatically."
                        : "No gateway serial was entered — you can pair and verify a device anytime from Hardware → Gateways after setup."}
                    </div>
                  </div>
                </div>
              )}

              {gwStatus === "idle" && (
                <div className="text-sm text-gray-500">No gateway serial was entered in the previous step — skip this and add devices anytime from Hardware → Gateways.</div>
              )}
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4" data-testid="step-6">
              <h2 className="text-xl font-bold mb-2">Invite Your Team</h2>
              <p className="text-sm text-gray-500">Add team members. To give someone full admin control of this organization, assign them <strong className="text-orange-300">Organization Admin</strong>.</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg px-3 py-2 text-xs text-orange-300">
                <strong>Tip:</strong> Organization Admins can independently manage sites, devices, users, and billing for this organization after login.
              </div>
              <div className="space-y-3">
                {data.invitations.map((inv, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`invite-row-${i}`}>
                    <div>
                      {i === 0 && <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Email</div>}
                      <Input value={inv.email} onChange={(e) => updateInvite(i, { email: e.target.value })} className="bg-gray-50 border-gray-200" placeholder="team@yourcompany.com" />
                    </div>
                    <div>
                      {i === 0 && <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Role</div>}
                      <Select value={inv.role} onValueChange={(v) => updateInvite(i, { role: v })}>
                        <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 text-gray-900">
                          {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <button onClick={() => removeInvite(i)} className={`text-red-400 hover:text-red-200 ${i === 0 ? "mt-5" : ""}`}><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                  </div>
                ))}
                {data.invitations.length < 10 && <Button onClick={addInvite} variant="outline" className="border-white/15 mt-2" data-testid="add-invite-btn"><Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Add Team Member</Button>}
              </div>
            </div>
          )}

          {/* FOOTER NAV */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-gray-200">
            <Button variant="ghost" onClick={back} disabled={step === 1} data-testid="onb-back">
              <ChevronLeft className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Back
            </Button>
            <div className="text-xs text-gray-500">Step {step} of {STEPS.length}</div>
            {step < STEPS.length ? (
              <Button onClick={next} className="f360-gradient border-0" data-testid="onb-next">Next <ChevronRight className="w-4 h-4 ml-1.5" strokeWidth={2.5} /></Button>
            ) : (
              <Button onClick={finish} disabled={busy} className="f360-gradient border-0" data-testid="onb-finish"><Check className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Finish Setup</Button>
            )}
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-gray-500">
          Need help? Contact <a className="text-orange-300 hover:underline" href="mailto:support@fire360pro.com">support@fire360pro.com</a>
        </div>
      </div>
    </div>
  );
}
